import { Link } from "react-router-dom";
import { ArrowRight, Target, Sparkles, Users } from "lucide-react";
import { Button } from "@/components/ui/button";
import NewsletterForm from "@/components/NewsletterForm";

const About = () => {
  return (
    <div className="min-h-screen pt-24 pb-32" data-testid="about-page">
      {/* Hero */}
      <section className="relative py-20 overflow-hidden">
        <div className="hero-glow top-0 left-1/4" />
        <div className="max-w-4xl mx-auto px-6 lg:px-8 relative z-10">
          <span className="text-gold text-xs uppercase tracking-widest font-body mb-4 block">The Mission</span>
          <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl text-[#EAEAEA] leading-[0.95] mb-8">
            Restoring coherence to the human system
          </h1>
          <p className="text-xl text-[#A1A1AA] leading-relaxed">
            We are working with a fundamental truth that modern culture has forgotten: 
            the human body is not a biochemical accident — it is a pressure-based cosmic instrument.
          </p>
        </div>
      </section>

      {/* Core Philosophy */}
      <section className="py-20 bg-paper">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <h2 className="font-heading text-3xl text-[#EAEAEA] mb-8">Every life is shaped by pressure</h2>
          <div className="space-y-6 text-[#A1A1AA] leading-relaxed">
            <p>
              Physical pressure. Emotional pressure. Social pressure. Gravitational pressure.
            </p>
            <p>
              Where pressure is mismanaged, identity fractures. Where pressure is integrated, presence emerges.
            </p>
            <p>
              Somalume is the science and practice of restoring pressure to its rightful role.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-12">
            <div className="p-6 border border-white/5 rounded-sm">
              <Target size={24} className="text-gold mb-4" />
              <h3 className="font-heading text-xl text-[#EAEAEA] mb-2">Sense without collapse</h3>
              <p className="text-[#A1A1AA] text-sm">Learn to meet pressure without being overwhelmed by it.</p>
            </div>
            <div className="p-6 border border-white/5 rounded-sm">
              <Sparkles size={24} className="text-gold mb-4" />
              <h3 className="font-heading text-xl text-[#EAEAEA] mb-2">Cross without fragmentation</h3>
              <p className="text-[#A1A1AA] text-sm">Navigate thresholds while maintaining coherence.</p>
            </div>
          </div>
        </div>
      </section>

      {/* What We're Building */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <span className="text-gold text-xs uppercase tracking-widest font-body mb-4 block">The Framework</span>
          <h2 className="font-heading text-3xl text-[#EAEAEA] mb-8">What we are doing</h2>
          
          <p className="text-[#A1A1AA] leading-relaxed mb-8">
            We are developing a unified framework that bridges cosmology and physiology, 
            physics and perception, biology and meaning, personal healing and collective coherence.
          </p>

          <div className="p-8 border border-gold/20 rounded-sm bg-gold/5">
            <h3 className="font-heading text-xl text-[#EAEAEA] mb-4">Somalume maps how:</h3>
            <ul className="space-y-3 text-[#A1A1AA]">
              <li className="flex items-start gap-3">
                <span className="text-gold mt-1">→</span>
                <span>Aether collapses into form</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-gold mt-1">→</span>
                <span>Pressure becomes structure</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-gold mt-1">→</span>
                <span>Structure stabilizes identity</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-gold mt-1">→</span>
                <span>Identity modulates the electromagnetic field</span>
              </li>
              <li className="flex items-start gap-3">
                <span className="text-gold mt-1">→</span>
                <span>The field shapes perception, emotion, and reality</span>
              </li>
            </ul>
            <p className="text-[#EAEAEA] mt-6 font-heading text-lg italic">
              This is not metaphor. It is architecture.
            </p>
          </div>
        </div>
      </section>

      {/* Themia */}
      <section className="py-20 bg-paper">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <span className="text-gold text-xs uppercase tracking-widest font-body mb-4 block">The Living Vehicle</span>
          <h2 className="font-heading text-3xl text-[#EAEAEA] mb-8">Themia</h2>
          
          <p className="text-[#A1A1AA] leading-relaxed mb-6">
            Themia is Somalume in motion. If Somalume is the body of knowledge, 
            Themia is the living ecosystem that allows it to breathe, move, and serve.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
            <div className="p-6 border border-white/5 rounded-sm">
              <Users size={24} className="text-gold mb-4" />
              <h3 className="font-heading text-lg text-[#EAEAEA] mb-2">Spaces of Coherence</h3>
              <p className="text-[#A1A1AA] text-sm">Physical, social, and perceptual environments that stabilize rather than fragment.</p>
            </div>
            <div className="p-6 border border-white/5 rounded-sm">
              <Target size={24} className="text-gold mb-4" />
              <h3 className="font-heading text-lg text-[#EAEAEA] mb-2">Resonant Nodes</h3>
              <p className="text-[#A1A1AA] text-sm">People who can hold pressure without reactivity and meet others without collapse.</p>
            </div>
          </div>

          <p className="text-[#A1A1AA] leading-relaxed italic">
            We are not building a movement. We are restoring natural law inside human systems. 
            When enough systems regain coherence, culture reorganizes automatically.
          </p>
        </div>
      </section>

      {/* About Ben */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <span className="text-gold text-xs uppercase tracking-widest font-body mb-4 block">The Architect</span>
          <h2 className="font-heading text-3xl text-[#EAEAEA] mb-8">Ben Manderson</h2>
          
          <div className="space-y-6 text-[#A1A1AA] leading-relaxed">
            <p>
              Ben Manderson is a systems thinker, breath practitioner, and architect of human coherence.
            </p>
            <p>
              His work bridges physics, biology, and lived experience to explore how pressure, breath, 
              and electromagnetic fields shape identity, health, and perception. Rather than treating 
              the human being as a problem to fix, Ben approaches the body as a living instrument — 
              capable of self-repair when its architecture is restored.
            </p>
            <p>
              Somalume emerged from a moment of clarity on November 15th, 2025 — a convergence of years 
              of study in breath work, biophysics, trauma resolution, and cosmological thinking. The name 
              itself carries its meaning: <em>Soma</em> (body) + <em>Lume</em> (light). Light embodied. 
              The body as a vessel for coherence.
            </p>
            <p>
              Through Somalume and Themia, he develops practical frameworks, spaces, and conversations 
              that help people rebuild stability from the inside out — not through belief, but through 
              embodied understanding.
            </p>
          </div>

          <div className="mt-8 p-6 border border-gold/20 rounded-sm bg-gold/5">
            <p className="font-heading text-xl text-[#EAEAEA]">
              "You do not need to fix yourself. You do not need to heal yourself. You need to 
              restore the conditions in which your system can return to natural order."
            </p>
            <p className="text-gold text-sm mt-4 uppercase tracking-widest">— Ben Manderson</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-paper">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl text-[#EAEAEA] mb-4">Begin Your Journey</h2>
          <p className="text-[#A1A1AA] mb-8 max-w-xl mx-auto">
            Explore the philosophy or dive into the complete library.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/philosophy">
              <Button className="bg-gold text-[#050505] hover:bg-gold-accent rounded-none h-14 px-10 uppercase text-xs tracking-widest btn-shine" data-testid="about-philosophy-btn">
                The 8 Pillars
                <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
            <Link to="/library">
              <Button variant="outline" className="border-white/20 text-[#EAEAEA] hover:bg-white/5 hover:border-gold/30 rounded-none h-14 px-10 uppercase text-xs tracking-widest" data-testid="about-explore-btn">
                Explore the Library
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Newsletter */}
      <section className="py-20">
        <div className="max-w-2xl mx-auto px-6 lg:px-8">
          <NewsletterForm />
        </div>
      </section>
    </div>
  );
};

export default About;
